import {
  ChangeDetectionStrategy,
  Component,
  computed,
  inject,
  OnInit,
  signal,
} from '@angular/core'
import { CommonModule } from '@angular/common'
import { HttpClient } from '@angular/common/http'
import { Router } from '@angular/router'
import { TranslateModule } from '@ngx-translate/core'
import { getBffOrigin } from '@app/core/commerce/commerce-api.constants'
import { AuthService } from '@app/core/services/auth.service'
import { TenantSwitchResetService } from '@app/core/services/tenant-switch-reset.service'

export interface TenantOption {
  id: string
  label: string
}

interface TenantSimpleApiItem {
  id: number | string
  name: string
}

@Component({
  selector: 'app-tenant-switcher',
  standalone: true,
  imports: [CommonModule, TranslateModule],
  changeDetection: ChangeDetectionStrategy.OnPush,
  template: `
    <div
      data-testid="tenant-switcher-container"
      class="flex items-center justify-between gap-2 rounded-md border border-slate-200 bg-slate-50 px-2 py-1 text-xs text-slate-700"
      [attr.aria-label]="'commerce.tenant.label' | translate"
    >
      <div class="flex items-center gap-1.5">
        <span class="material-symbols-outlined text-sm text-sky-600" aria-hidden="true">
          domain
        </span>
        <span class="font-medium">{{ 'commerce.tenant.label' | translate }}:</span>
        <span data-testid="tenant-readonly" class="font-semibold text-slate-800">{{
          selectedTenant()
        }}</span>
      </div>
      <div class="flex items-center gap-1">
        <select
          data-testid="topbar-tenant-switcher"
          [attr.aria-label]="'commerce.tenant.switch_aria' | translate"
          class="rounded border border-slate-300 bg-white px-1.5 py-0.5 text-xs font-medium text-slate-700 focus:border-sky-500 focus:outline-none cursor-pointer"
          [value]="selectedTenant()"
          (change)="onTenantChange($event)"
        >
          @for (t of tenantOptions(); track t.id) {
            <option [value]="t.id">{{ t.label }}</option>
          }
        </select>
        <button
          data-testid="topbar-manage-tenants-btn"
          type="button"
          [attr.title]="'user_settings.actions.manage_tenants' | translate"
          [attr.aria-label]="'user_settings.actions.manage_tenants' | translate"
          class="flex items-center justify-center rounded p-0.5 text-slate-500 hover:bg-slate-200 hover:text-sky-700 cursor-pointer border-none bg-transparent"
          (click)="openTenantsAdmin()"
        >
          <span class="material-symbols-outlined text-sm" aria-hidden="true">settings</span>
        </button>
      </div>
    </div>
  `,
})
export class TenantSwitcherComponent implements OnInit {
  private readonly authService = inject(AuthService)
  private readonly tenantSwitchReset = inject(TenantSwitchResetService, { optional: true })
  private readonly http = inject(HttpClient, { optional: true })
  private readonly router = inject(Router, { optional: true })

  readonly tenantOptions = signal<TenantOption[]>([
    { id: '1', label: '#1 UniRetail' },
    { id: '2', label: '#2 KidsPlaza' },
    { id: '3', label: '#3 Franchise' },
  ])
  readonly selectedTenant = signal<string>('1')
  readonly tenantLabel = computed(() => this.selectedTenant() || '1')

  constructor() {
    const id = this.authService.getTenantId() || '1'
    this.selectedTenant.set(id)
    if (!this.tenantOptions().some((o) => o.id === id)) {
      this.tenantOptions.update((list) => [{ id, label: `#${id} Active` }, ...list])
    }
    this.authService.setActiveTenantId(id)
  }

  ngOnInit(): void {
    this.loadDynamicTenants()
    this.authService.tenantCatalogChanged$?.subscribe(() => {
      this.loadDynamicTenants()
    })
    this.authService.tenantSwitched$?.subscribe((id) => {
      if (id && id !== this.selectedTenant()) {
        this.selectedTenant.set(id)
      }
    })
  }

  public loadDynamicTenants(): void {
    if (!this.http) {
      return
    }
    this.http
      .get<{ code?: number; data?: TenantSimpleApiItem[] }>(
        `${getBffOrigin()}/api/hrm/admin-api/oms/tenant/simple-list`,
      )
      .subscribe({
        next: (resp) => {
          const items = resp?.data
          if (!Array.isArray(items) || items.length === 0) {
            return
          }
          const mapped: TenantOption[] = items.map((item) => {
            const idStr = String(item.id)
            const rawName = (item.name || '').replace(/\s*\(default\)\s*/i, '').trim()
            const label = rawName.startsWith(`#${idStr}`)
              ? rawName
              : `#${idStr} ${rawName || 'Tenant'}`
            return { id: idStr, label }
          })
          const activeId = this.selectedTenant()
          if (!mapped.some((m) => m.id === activeId)) {
            if (Number(activeId) > 3) {
              this.selectedTenant.set('1')
              this.authService.setActiveTenantId('1', true)
            } else {
              mapped.unshift({ id: activeId, label: `#${activeId} Active` })
            }
          }
          this.tenantOptions.set(mapped)
        },
        error: () => {
          // Keep default fallback options (#1 UniRetail, #2 KidsPlaza, #3 Franchise)
        },
      })
  }

  onTenantChange(event: Event): void {
    const value = (event.target as HTMLSelectElement).value
    const previous = this.selectedTenant()
    this.selectedTenant.set(value)
    this.authService.setActiveTenantId(value, true)
    this.tenantSwitchReset?.resetAllTenantCaches()
    if (previous !== value) {
      this.refreshCurrentRoute()
    }
  }

  openTenantsAdmin(): void {
    this.router?.navigateByUrl('/platform/tenants')
  }

  private refreshCurrentRoute(): void {
    if (!this.router) {
      return
    }
    const currentUrl = this.router.url
    if (!currentUrl || currentUrl === '/login' || currentUrl === '/home' || currentUrl.startsWith('/home?')) {
      return
    }
    this.router.navigateByUrl('/home', { skipLocationChange: true }).then(() => {
      this.router?.navigateByUrl(currentUrl)
    })
  }
}
